import { createContext, useContext, useState, useEffect } from "react";
import { getApps, getTenantApps } from "../Services/AppService";

type TenantApp = {
    name: string;
    type: 'external' | 'internal'; // or whatever values you expect
    link: string;
    slug: string;
};
  
// Provide a default value for the context
const AppContext = createContext<{
    apps: string[];
    tenantApps: TenantApp[];
    findIcon: (slug: string) => string;
    imageUrl: (image_url: string) => string;
    appLink: (app: { type: string; link: string; slug: string }) => string;
} | undefined>(undefined); // Default value can be undefined or an object with default values

export const AppProvider = ({ children }: { children: React.ReactNode }) => {
    const [apps, setApps] = useState<string[]>([]);
    const [tenantApps, setTenantApps] = useState<TenantApp[]>([]);

    // Fetch apps and tenant apps on mount
    useEffect(() => {
        const fetchAllApps = async () => {
            // Check if apps are stored in localStorage
            const storedApps = localStorage.getItem('apps');
            const storedTenantApps = localStorage.getItem('tenantApps');

            if (storedApps && storedTenantApps) {
                // If the data is found in localStorage, parse and use it
                setApps(JSON.parse(storedApps));
                setTenantApps(JSON.parse(storedTenantApps));
            } else {
                // Otherwise, fetch data from the API
                try {
                    const [appsData, tenantAppsData] = await Promise.all([getApps(), getTenantApps()]);

                    // Save the fetched data to localStorage for future use
                    localStorage.setItem('apps', JSON.stringify(appsData));
                    localStorage.setItem('tenantApps', JSON.stringify(tenantAppsData));

                    // Set the state with the fetched data
                    setApps(appsData);
                    setTenantApps(tenantAppsData);
                } catch (error) {
                    console.error('Error fetching apps:', error);
                }
            }
        };

        fetchAllApps();
    }, []); // Empty dependency array means this will only run on mount


    const findIcon = (slug: string): string => {
        const iconMap: { [key: string]: string } = {
            'health-check': 'mdi mdi-shield-cross-outline',
            'energy-view': 'mdi mdi-briefcase-search-outline',
            'database': 'ti-server',
            'notepad': 'ti-notepad',
            'wallet': 'ti-wallet',
            'conversion': 'mdi mdi-account-convert',
            'forum': 'mdi mdi-forum-outline',
            'energy-tracker': 'fa-solid fa-chart-line',
            'datasheets': 'mdi mdi-script-text-outline',
            'bills': 'fa-regular fa-file-invoice',
            'benchmarks': 'mdi mdi-chart-bar',
            'technology': 'ti-bolt',
            'podcast': 'mdi mdi-podcast'
        };
        return iconMap[slug] || 'ti-layout-grid2';
    };

    const imageUrl = (image_url: string): string => {
        if (image_url) {
            return `background-image: linear-gradient(0deg, rgba(50, 40, 40, 0.612), black), url('${image_url}'); background-size: 100% 100%;`;
        }
        return '';
    };

    const appLink = (app: { type: string; link: string; slug: string }): string => {
        return app.type === 'external' 
            ? `${app.link}` 
            : `/${app.slug}`;
    };

    return (
        <AppContext.Provider value={{ apps, tenantApps, findIcon, imageUrl, appLink }}>
            {children}
        </AppContext.Provider>
    );
};

// Custom hook for easy usage
export const useApp = () => {
    const context = useContext(AppContext);
    if (!context) {
        throw new Error("useApp must be used within an AppProvider");
    }
    return context;
};
